import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Logo, Btn } from "@/components/kit";
import { NotificationBell } from "@/components/NotificationBell";
import { LogOut, LayoutGrid } from "lucide-react";
import { clsx } from "clsx";

export default function Shell({ title, subtitle, nav, active, onNav, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="bd-page flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-[#2E1256] bg-[#100226]/80 backdrop-blur-xl min-h-screen sticky top-0">
        <div className="p-5 border-b border-[#2E1256]"><Logo size={36} /></div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <button key={n.key} data-testid={`nav-${n.key}`} onClick={() => onNav(n.key)}
              className={clsx("w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200",
                active === n.key ? "bg-gradient-to-r from-[#FF5E36]/20 to-[#FF2A6D]/10 text-white border border-[#FF2A6D]/30" : "text-purple-300 hover:text-white hover:bg-[#1E0A3E]")}>
              <n.icon size={18} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-[#2E1256]">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="text-xs text-purple-400 truncate">{user?.title || user?.role}{user?.department_name ? ` · ${user.department_name}` : ""}</div>
          </div>
          <Btn variant="outline" className="w-full mb-2 justify-start" data-testid="back-to-portals" onClick={() => navigate("/portals")}><LayoutGrid size={16} /> Back to portals</Btn>
          <Btn variant="secondary" className="w-full" data-testid="logout-btn" onClick={logout}><LogOut size={16} /> Sign out</Btn>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 border-b border-[#2E1256] bg-[#160733]/85 backdrop-blur-xl">
          <div className="flex items-center justify-between px-5 lg:px-8 py-4">
            <div>
              <h1 className="font-head text-xl sm:text-2xl font-bold text-white">{title}</h1>
              {subtitle && <p className="text-sm text-purple-300/70">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-1">
              <NotificationBell />
              <div className="lg:hidden flex items-center gap-1">
                <Btn variant="ghost" data-testid="back-to-portals-mobile" onClick={() => navigate("/portals")}><LayoutGrid size={16} /></Btn>
                <Btn variant="ghost" data-testid="logout-btn-mobile" onClick={logout}><LogOut size={16} /></Btn>
              </div>
            </div>
          </div>
          <div className="lg:hidden flex gap-1 overflow-x-auto px-3 pb-3">
            {nav.map((n) => (
              <button key={n.key} onClick={() => onNav(n.key)} data-testid={`mnav-${n.key}`}
                className={clsx("shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium", active === n.key ? "bg-[#FF2A6D]/20 text-white" : "text-purple-300")}>{n.label}</button>
            ))}
          </div>
        </header>
        <main className="p-5 lg:p-8 max-w-7xl mx-auto bd-fade-up">{children}</main>
      </div>
    </div>
  );
}
